import React, { useEffect, useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Nav, Navbar, Row, Col } from 'react-bootstrap';
import { navbarBreakPoint, topNavbarBreakpoint } from 'config';
import Flex from 'components/common/flex';
import Logo from 'components/common/logo';
import NavbarVerticalMenu from './navbarVerticalMenu';
import ToggleButton from './toggleButton';
import routes from 'routes/siteMaps';
import { rootPaths } from 'routes/paths';
import bgNavbar from 'assets/img/generic/bg-navbar.png';
import { useAppContext } from 'providers/appProvider';
import axios from 'axios';
import { useTranslation } from "react-i18next";

const NavbarVertical = () => {
  const [userRole, setUserRole] = useState(null);
  const [claimData, setClaimData] = useState([]);
  const { t } = useTranslation();
  const {
    config: { navbarStyle, isNavbarVerticalCollapsed, showBurgerMenu },
    isEmployeeView,
    isAttendanceEnabled
  } = useAppContext();

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  const commonRoutes = ['menu.profile']; // always visible routes
  const isClientUser = localStorage.getItem('UserRole') === 'Client';

  // Collapse effect
  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 250);
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  // Mouse hover to expand collapsed navbar
  let time = null;
  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };
  const handleMouseLeave = () => {
    clearTimeout(time);
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
  };

  // Fetch user role and claim
  useEffect(() => {
    const fetchRoleAndClaims = async () => {
      try {
        let roleId;
        if (isEmployeeView) {          // ← now uses context value, not sessionStorage
          const rolesResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/roleRoute/roles`, {
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
          });
          const roles = rolesResponse.data;
          const employeeRole = roles.find(r => r.Name === "Employee");
          if (employeeRole) {
            roleId = employeeRole.RoleId;
          }
        } else {
          const userId = localStorage.getItem("UserId");
          const userResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`, {
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
          });
          roleId = userResponse.data.Role;
        }

        setUserRole(roleId);

        const claimsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/claimRoute/claims`, {
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
        });
        const filteredClaims = claimsResponse.data.filter(
          claim => claim.RoleId === roleId
        );
        setClaimData(filteredClaims || []);
      } catch (error) {
        console.error("Error fetching role or claims:", error);
      }
    };

    fetchRoleAndClaims();
  }, [isEmployeeView]);  // ← this is the key change

  // Helper to check if claim exists
  const hasClaim = claimType => claimData.some(claim => claim.Type === claimType);

  // Filter routes based on claims
  const filterRoutesByClaims = routes => {
    return routes
      .map(route => {
        if (!route.children) return route;

        const filteredChildren = route.children.filter(child => {
          const claimType = child.claimLabel || child.name;
          return hasClaim(claimType);
        });

        const alwaysVisibleChildren = route.children.filter(child =>
          commonRoutes.includes(child.name || child.claimLabel)
        );

        const uniqueChildren = route.children.filter(child => {
          const claimType = child.claimLabel || child.name;
          const isCommonRoute = commonRoutes.includes(child.name || child.claimLabel);
          return hasClaim(claimType) || isCommonRoute;
        });

        return { ...route, children: uniqueChildren };
      })
      .filter(route => route.children && route.children.length > 0);
  };

  const filteredRoutes = useMemo(() => filterRoutesByClaims(routes), [claimData, isClientUser]);

  const attendanceFilteredRoutes = useMemo(() => {
    if (isAttendanceEnabled) return filteredRoutes;
    return filteredRoutes.map((section) => ({
      ...section,
      children: section.children?.filter(
        (child) => child.to !== rootPaths.myTimesheetRoot && child.to !== rootPaths.attendanceRoot
      ),
    }));
  }, [filteredRoutes, isAttendanceEnabled]);

  const NavbarLabel = ({ label }) => (
    <Nav.Item as="li">
      <Row className="mt-3 mb-2 navbar-vertical-label-wrapper">
        <Col xs="auto" className="navbar-vertical-label">
          {label}
        </Col>
        <Col className="ps-0">
          <hr className="mb-0 navbar-vertical-divider" />
        </Col>
      </Row>
    </Nav.Item>
  );

  return (
    <Navbar
      id="customWrapperNav"
      expand={navbarBreakPoint}
      className={classNames('navbar-vertical', {
        [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent',
      })}
      variant="light"
    >
      <Flex alignItems="center">
        <ToggleButton />
        <Logo at="navbar-vertical" width={40} />
      </Flex>

      <Navbar.Collapse
        in={showBurgerMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundImage:
            navbarStyle === 'vibrant'
              ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
              : 'none',
        }}
      >
        <div className="navbar-vertical-content scrollbar border-end">
          <Nav className="flex-column" as="ul">
            {attendanceFilteredRoutes
              .filter(route => route.label !== 'Helpdesk')
              .map(route => (
                <Fragment key={route.label}>
                  {!route.labelDisable && (
                    <NavbarLabel label={t(route.label)} />
                  )}
                  <NavbarVerticalMenu routes={route.children} />
                </Fragment>
              ))}
          </Nav>

          <div className="navbar-vertical-helpdesk mt-auto">
            <Nav className="flex-column" as="ul">
              {filteredRoutes
                .filter(route => route.label === 'Helpdesk')
                .map(route => (
                  <Fragment key={route.label}>
                    {!route.labelDisable && (
                      <NavbarLabel label={t(route.label)} />
                    )}
                    <NavbarVerticalMenu routes={route.children} />
                  </Fragment>
                ))}
            </Nav>
          </div>
        </div>
      </Navbar.Collapse>
    </Navbar>
  );
};

NavbarVertical.propTypes = {
  label: PropTypes.string,
};

export default NavbarVertical;
